'use client';

import React, { useState, useEffect, useCallback } from 'react';
import { createClient } from '@/lib/supabase/client';
import AppLayout from '@/components/AppLayout';
import Icon from '@/components/ui/AppIcon';

// Create supabase client once outside the component to avoid infinite re-render
const supabase = createClient();

interface TurnoReperibilita {
  id: string;
  tecnico_id: string;
  tipo: string;
  data_inizio: string;
  data_fine: string;
  telefono_reperibilita: string;
  email_reperibilita: string;
  note: string;
  attivo: boolean;
  created_at: string;
  user_profiles?: { full_name: string; email: string } | null;
}

interface UserProfile {
  id: string;
  full_name: string;
  email: string;
}

const TURNO_TIPO_CONFIG: Record<string, { label: string; bg: string; text: string }> = {
  mattina: { label: 'Mattina', bg: 'bg-yellow-500/20', text: 'text-yellow-400' },
  pomeriggio: { label: 'Pomeriggio', bg: 'bg-orange-500/20', text: 'text-orange-400' },
  notte: { label: 'Notte', bg: 'bg-blue-500/20', text: 'text-blue-400' },
  festivo: { label: 'Festivo', bg: 'bg-purple-500/20', text: 'text-purple-400' },
  reperibilita: { label: 'Reperibilità', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
};

const DAYS = ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'];

export default function TurniReperibilitaContent() {
  const [turni, setTurni] = useState<TurnoReperibilita[]>([]);
  const [users, setUsers] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [editItem, setEditItem] = useState<TurnoReperibilita | null>(null);
  const [saving, setSaving] = useState(false);
  const [viewMode, setViewMode] = useState<'lista' | 'calendario'>('lista');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);
  const [currentWeekStart, setCurrentWeekStart] = useState(() => {
    const d = new Date();
    const day = d.getDay();
    const diff = d.getDate() - day + (day === 0 ? -6 : 1);
    d.setDate(diff);
    d.setHours(0, 0, 0, 0);
    return d;
  });
  const [form, setForm] = useState({
    tecnico_id: '',
    tipo: 'reperibilita',
    data_inizio: '',
    data_fine: '',
    telefono_reperibilita: '',
    email_reperibilita: '',
    note: '',
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [turniRes, usersRes] = await Promise.all([
        supabase.from('turni_reperibilita').select('*, user_profiles(full_name, email)').order('data_inizio', { ascending: false }),
        supabase.from('user_profiles').select('id, full_name, email').order('full_name'),
      ]);
      if (turniRes.error) throw turniRes.error;
      if (usersRes.error) throw usersRes.error;
      setTurni((turniRes.data as TurnoReperibilita[]) || []);
      setUsers((usersRes.data as UserProfile[]) || []);
    } catch (e: unknown) {
      setError(e instanceof Error ? e.message : 'Errore caricamento');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const saveTurno = async () => {
    if (!form.tecnico_id || !form.data_inizio || !form.data_fine) {
      alert('Compilare tecnico, data inizio e data fine');
      return;
    }
    if (new Date(form.data_fine) <= new Date(form.data_inizio)) {
      alert('La data fine deve essere successiva alla data inizio');
      return;
    }
    if (form.telefono_reperibilita && !/^[\d\s\+\-\(\)]{6,20}$/.test(form.telefono_reperibilita)) {
      alert('Formato telefono non valido');
      return;
    }
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (editItem) {
        await supabase.from('turni_reperibilita').update({ ...form }).eq('id', editItem.id);
      } else {
        await supabase.from('turni_reperibilita').insert({ ...form, created_by: user?.id });
      }
      setShowForm(false);
      setEditItem(null);
      setForm({ tecnico_id: '', tipo: 'reperibilita', data_inizio: '', data_fine: '', telefono_reperibilita: '', email_reperibilita: '', note: '' });
      await fetchData();
    } catch (e: unknown) {
      alert(e instanceof Error ? e.message : 'Errore salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const deleteTurno = async (id: string) => {
    await supabase.from('turni_reperibilita').delete().eq('id', id);
    setDeleteConfirmId(null);
    await fetchData();
  };

  const toggleAttivo = async (id: string, attivo: boolean) => {
    await supabase.from('turni_reperibilita').update({ attivo: !attivo }).eq('id', id);
    await fetchData();
  };

  const formatDate = (d: string) => new Date(d).toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
  const formatDateTime = (d: string) => new Date(d).toLocaleString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' });

  // Calendar week view
  const weekDays = Array.from({ length: 7 }, (_, i) => {
    const d = new Date(currentWeekStart);
    d.setDate(d.getDate() + i);
    return d;
  });

  const getTurniForDay = (day: Date) => {
    return turni.filter(t => {
      const start = new Date(t.data_inizio);
      const end = new Date(t.data_fine);
      return start <= day && end >= day;
    });
  };

  const turnoCorrente = turni.find(t => {
    const now = new Date();
    return new Date(t.data_inizio) <= now && new Date(t.data_fine) >= now && t.attivo;
  });

  const openCreate = () => {
    setEditItem(null);
    setForm({ tecnico_id: '', tipo: 'reperibilita', data_inizio: '', data_fine: '', telefono_reperibilita: '', email_reperibilita: '', note: '' });
    setShowForm(true);
  };

  const openEdit = (t: TurnoReperibilita) => {
    setEditItem(t);
    setForm({
      tecnico_id: t.tecnico_id,
      tipo: t.tipo,
      data_inizio: t.data_inizio.slice(0, 16),
      data_fine: t.data_fine.slice(0, 16),
      telefono_reperibilita: t.telefono_reperibilita || '',
      email_reperibilita: t.email_reperibilita || '',
      note: t.note || '',
    });
    setShowForm(true);
  };

  return (
    <AppLayout>
      <div className="p-6 space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-white flex items-center gap-2">
              <Icon name="CalendarDaysIcon" size={28} className="text-emerald-400" />
              Turni Reperibilità
            </h1>
            <p className="text-muted-color text-sm mt-1">Gestione calendario turni tecnici reperibili</p>
          </div>
          <button onClick={openCreate}
            className="flex items-center gap-2 px-4 py-2 bg-emerald-500 text-white rounded-xl font-semibold text-sm hover:bg-emerald-600 transition-colors">
            <Icon name="PlusIcon" size={16} />
            Nuovo Turno
          </button>
        </div>

        {/* Tecnico di turno corrente */}
        {turnoCorrente && (
          <div className="bg-emerald-500/10 border border-emerald-500/30 rounded-2xl p-5">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-full bg-emerald-500/20 flex items-center justify-center">
                <Icon name="UserCircleIcon" size={28} className="text-emerald-400" />
              </div>
              <div>
                <p className="text-xs text-emerald-400 font-semibold uppercase tracking-wider">Tecnico di Turno Ora</p>
                <p className="text-xl font-bold text-white">{turnoCorrente.user_profiles?.full_name || 'N/D'}</p>
                <div className="flex items-center gap-4 mt-1 text-sm text-muted-color">
                  {turnoCorrente.telefono_reperibilita && (
                    <span className="flex items-center gap-1">
                      <Icon name="PhoneIcon" size={14} />
                      {turnoCorrente.telefono_reperibilita}
                    </span>
                  )}
                  {turnoCorrente.email_reperibilita && (
                    <span className="flex items-center gap-1">
                      <Icon name="EnvelopeIcon" size={14} />
                      {turnoCorrente.email_reperibilita}
                    </span>
                  )}
                  <span className="flex items-center gap-1">
                    <Icon name="ClockIcon" size={14} />
                    Fino al {formatDate(turnoCorrente.data_fine)}
                  </span>
                </div>
              </div>
            </div>
          </div>
        )}

        {/* View Toggle */}
        <div className="flex gap-1 bg-surface rounded-xl p-1 border border-border w-fit">
          {[
            { v: 'lista', l: 'Lista', icon: 'ListBulletIcon' },
            { v: 'calendario', l: 'Calendario', icon: 'CalendarIcon' },
          ].map(({ v, l, icon }) => (
            <button key={v} onClick={() => setViewMode(v as any)}
              className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold transition-colors ${viewMode === v ? 'bg-sky-500/20 text-sky-400' : 'text-muted-color hover:text-white'}`}>
              <Icon name={icon as any} size={16} />
              {l}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : error ? (
          <div className="bg-red-500/10 border border-red-500/30 rounded-xl p-4 text-red-400">{error}</div>
        ) : (
          <>
            {viewMode === 'calendario' && (
              <div className="bg-surface border border-border rounded-2xl overflow-hidden">
                <div className="flex items-center justify-between p-4 border-b border-border">
                  <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() - 7); setCurrentWeekStart(d); }}
                    className="p-2 hover:bg-surface-elevated rounded-lg transition-colors">
                    <Icon name="ChevronLeftIcon" size={18} className="text-muted-color" />
                  </button>
                  <span className="font-semibold text-white">
                    {formatDate(currentWeekStart.toISOString())} — {formatDate(weekDays[6].toISOString())}
                  </span>
                  <button onClick={() => { const d = new Date(currentWeekStart); d.setDate(d.getDate() + 7); setCurrentWeekStart(d); }}
                    className="p-2 hover:bg-surface-elevated rounded-lg transition-colors">
                    <Icon name="ChevronRightIcon" size={18} className="text-muted-color" />
                  </button>
                </div>
                <div className="grid grid-cols-7">
                  {DAYS.map(d => (
                    <div key={d} className="p-3 text-center text-xs font-semibold text-muted-color border-b border-border">{d}</div>
                  ))}
                  {weekDays.map((day, i) => {
                    const dayTurni = getTurniForDay(day);
                    const isToday = day.toDateString() === new Date().toDateString();
                    return (
                      <div key={i} className={`min-h-24 p-2 border-b border-r border-border last:border-r-0 ${isToday ? 'bg-sky-500/5' : ''}`}>
                        <div className={`text-sm font-semibold mb-1 ${isToday ? 'text-sky-400' : 'text-muted-color'}`}>
                          {day.getDate()}
                        </div>
                        {dayTurni.map(t => {
                          const cfg = TURNO_TIPO_CONFIG[t.tipo] || TURNO_TIPO_CONFIG.reperibilita;
                          return (
                            <div key={t.id} className={`text-xs px-1.5 py-1 rounded mb-1 ${cfg.bg} ${cfg.text} truncate`}>
                              {t.user_profiles?.full_name?.split(' ')[0] || 'N/D'}
                            </div>
                          );
                        })}
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            {viewMode === 'lista' && (
              <div className="space-y-3">
                {turni.length === 0 ? (
                  <div className="text-center py-12 text-muted-color">Nessun turno pianificato</div>
                ) : turni.map(t => {
                  const cfg = TURNO_TIPO_CONFIG[t.tipo] || TURNO_TIPO_CONFIG.reperibilita;
                  const isActive = new Date(t.data_inizio) <= new Date() && new Date(t.data_fine) >= new Date() && t.attivo;
                  return (
                    <div key={t.id} className={`bg-surface border rounded-2xl p-4 ${isActive ? 'border-emerald-500/40' : 'border-border'}`}>
                      <div className="flex items-start justify-between gap-3">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 rounded-full flex items-center justify-center ${cfg.bg}`}>
                            <Icon name="UserCircleIcon" size={22} className={cfg.text} />
                          </div>
                          <div>
                            <div className="flex items-center gap-2">
                              <p className="text-sm font-semibold text-white">{t.user_profiles?.full_name || 'N/D'}</p>
                              <span className={`text-xs px-2 py-0.5 rounded-full ${cfg.bg} ${cfg.text}`}>{cfg.label}</span>
                              {isActive && <span className="text-xs bg-emerald-500/20 text-emerald-400 px-2 py-0.5 rounded-full animate-pulse">● In turno</span>}
                            </div>
                            <p className="text-xs text-muted-color mt-0.5">
                              {formatDateTime(t.data_inizio)} → {formatDateTime(t.data_fine)}
                            </p>
                            <div className="flex gap-3 mt-1 text-xs text-muted-color">
                              {t.telefono_reperibilita && <span>📞 {t.telefono_reperibilita}</span>}
                              {t.email_reperibilita && <span>✉️ {t.email_reperibilita}</span>}
                            </div>
                          </div>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => toggleAttivo(t.id, t.attivo)}
                            className={`text-xs px-2 py-1 rounded-lg transition-colors ${t.attivo ? 'bg-emerald-500/10 text-emerald-400' : 'bg-gray-500/10 text-gray-400'}`}
                          >
                            {t.attivo ? 'Attivo' : 'Inattivo'}
                          </button>
                          <button onClick={() => openEdit(t)} className="p-1.5 text-muted-color hover:text-sky-400 hover:bg-sky-500/10 rounded-lg transition-colors" title="Modifica">
                            <Icon name="PencilIcon" size={15} />
                          </button>
                          <button onClick={() => setDeleteConfirmId(t.id)} className="p-1.5 text-muted-color hover:text-red-400 hover:bg-red-500/10 rounded-lg transition-colors" title="Elimina">
                            <Icon name="TrashIcon" size={15} />
                          </button>
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </>
        )}
      </div>

      {/* Delete Confirm */}
      {deleteConfirmId && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
          <div className="bg-surface border border-border rounded-2xl w-full max-w-sm shadow-2xl p-6">
            <h3 className="text-lg font-bold text-white mb-2">Elimina Turno</h3>
            <p className="text-secondary-color text-sm mb-5">Sei sicuro di voler eliminare questo turno?</p>
            <div className="flex gap-3">
              <button onClick={() => setDeleteConfirmId(null)} className="flex-1 py-2.5 rounded-xl border border-border text-sm font-semibold text-secondary-color hover:text-white hover:bg-surface-elevated transition-all">Annulla</button>
              <button onClick={() => deleteTurno(deleteConfirmId)} className="flex-1 py-2.5 rounded-xl bg-red-500 hover:bg-red-400 text-white text-sm font-bold transition-all">Elimina</button>
            </div>
          </div>
        </div>
      )}

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50 p-4">
          <div className="bg-surface rounded-2xl border border-border p-6 w-full max-w-lg shadow-2xl">
            <div className="flex items-center justify-between mb-5">
              <h3 className="text-lg font-semibold text-white">{editItem ? 'Modifica Turno' : 'Nuovo Turno'}</h3>
              <button onClick={() => { setShowForm(false); setEditItem(null); }} className="text-muted-color hover:text-white"><Icon name="XMarkIcon" size={20} /></button>
            </div>
            <div className="space-y-4">
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Tecnico *</label>
                <select value={form.tecnico_id} onChange={e => setForm(p => ({ ...p, tecnico_id: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                  <option value="">— Seleziona tecnico —</option>
                  {users.map(u => <option key={u.id} value={u.id}>{u.full_name || u.email}</option>)}
                </select>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Tipo Turno</label>
                <select value={form.tipo} onChange={e => setForm(p => ({ ...p, tipo: e.target.value }))}
                  className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500">
                  {Object.entries(TURNO_TIPO_CONFIG).map(([k, v]) => <option key={k} value={k}>{v.label}</option>)}
                </select>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Data Inizio *</label>
                  <input type="datetime-local" value={form.data_inizio} onChange={e => setForm(p => ({ ...p, data_inizio: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Data Fine *</label>
                  <input type="datetime-local" value={form.data_fine} onChange={e => setForm(p => ({ ...p, data_fine: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Telefono Reperibilità</label>
                  <input value={form.telefono_reperibilita} onChange={e => setForm(p => ({ ...p, telefono_reperibilita: e.target.value }))}
                    placeholder="+39 333 1234567"
                    className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
                <div>
                  <label className="block text-xs font-semibold text-muted-color mb-1">Email Reperibilità</label>
                  <input type="email" value={form.email_reperibilita} onChange={e => setForm(p => ({ ...p, email_reperibilita: e.target.value }))}
                    className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500" />
                </div>
              </div>
              <div>
                <label className="block text-xs font-semibold text-muted-color mb-1">Note</label>
                <textarea value={form.note} onChange={e => setForm(p => ({ ...p, note: e.target.value }))} rows={2}
                  className="w-full bg-surface-elevated border border-border rounded-xl px-3 py-2.5 text-sm text-white focus:outline-none focus:border-sky-500 resize-none" />
              </div>
            </div>
            <div className="flex gap-3 pt-4">
              <button onClick={() => { setShowForm(false); setEditItem(null); }} className="flex-1 px-4 py-2.5 border border-border text-secondary-color rounded-xl hover:bg-surface-elevated transition-colors text-sm">Annulla</button>
              <button onClick={saveTurno} disabled={saving} className="flex-1 px-4 py-2.5 bg-emerald-500 text-white rounded-xl hover:bg-emerald-600 transition-colors text-sm font-semibold disabled:opacity-50">
                {saving ? 'Salvataggio...' : editItem ? 'Aggiorna' : 'Salva'}
              </button>
            </div>
          </div>
        </div>
      )}
    </AppLayout>
  );
}
